import { FormHelperText, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { VALIDATION_CONSTANTS } from "@/common/constants/validation";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";

import AppIconButton from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import ConfirmationModalWithTrigger, {
  ConfirmationModalWithTriggerProps,
} from "../ConfirmationModalWithTrigger";

interface Props extends Omit<
  ConfirmationModalWithTriggerProps,
  "trigger" | "body" | "title" | "onConfirm"
> {
  trigger?: ConfirmationModalWithTriggerProps["trigger"];
  onConfirm?: (externalNumber: string) => Promise<void>;
  initialValues: { externalNumber?: string | null | undefined };
}

const BaseEditExternalNumberModal = ({
  trigger,
  onConfirm,
  initialValues,
  ...otherProps
}: Props) => {
  const { enqueueSnackbar } = useAppSnackbar();
  const [externalNumber, setExternalNumber] = useState(initialValues?.externalNumber || "");

  return (
    <>
      <ConfirmationModalWithTrigger
        trigger={
          trigger || (
            <AppIconButton size='small'>
              <AppIcon of='edit' fontSize='small' />
            </AppIconButton>
          )
        }
        title='Edit external number'
        body={({ error }) => {
          return (
            <Stack>
              <Typography>
                To update external number enter new value and confirm the action.
              </Typography>

              <TextField
                autoFocus
                type='text'
                margin='dense'
                value={externalNumber}
                slotProps={{
                  htmlInput: {
                    maxLength: VALIDATION_CONSTANTS.ExternalNumberMaxLength,
                  },
                }}
                onChange={(e) => setExternalNumber(e.target.value)}
              />

              {error?.hasErrors && (
                <FormHelperText error>{error.getErrorsAsString()}</FormHelperText>
              )}
            </Stack>
          );
        }}
        onConfirm={async () => {
          await onConfirm?.(externalNumber);
          enqueueSnackbar("External number updated.", { variant: "success" });
        }}
        {...otherProps}
      />
    </>
  );
};

export default BaseEditExternalNumberModal;
